import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Clock, 
  CheckCircle,
  XCircle,
  Calendar,
  Loader2,
  Eye,
  Building2
} from 'lucide-react';
import { getMyApplications } from '../services/api';

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const res = await getMyApplications();
      setApplications(res.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Ausstehend', variant: 'secondary', icon: Clock },
      reviewing: { label: 'In Prüfung', variant: 'default', icon: Eye },
      interview: { label: 'Interview', variant: 'success', icon: Calendar },
      accepted: { label: 'Angenommen', variant: 'success', icon: CheckCircle },
      rejected: { label: 'Abgelehnt', variant: 'destructive', icon: XCircle }
    };
    const config = statusMap[status] || statusMap.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
        status === 'pending' ? 'bg-gray-100 text-gray-700' :
        status === 'reviewing' ? 'bg-blue-100 text-blue-700' :
        status === 'interview' ? 'bg-green-100 text-green-700' :
        status === 'accepted' ? 'bg-green-100 text-green-700' :
        'bg-red-100 text-red-700'
      }`}>
        <config.icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="applications-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Meine Bewerbungen</h1>
          <p className="text-muted-foreground mt-1">
            {applications.length} Bewerbungen
          </p>
        </div>

        {/* Applications List */}
        {applications.length > 0 ? (
          <div className="grid gap-4">
            {applications.map((app) => (
              <Card 
                key={app.application_id} 
                className="hover:shadow-md transition-all"
                data-testid={`application-${app.application_id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{app.job_title || 'Position'}</h3>
                        <p className="text-sm text-muted-foreground">{app.company_name || 'Unternehmen'}</p>
                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Beworben am {new Date(app.created_at).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(app.status)}
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <Link to={`/jobs/${app.job_id}`}>
                      <Button variant="outline" size="sm" className="rounded-full">
                        <Eye className="w-4 h-4 mr-2" />
                        Job ansehen
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Keine Bewerbungen</h3>
              <p className="text-muted-foreground">
                Sie haben sich noch auf keine Stellen beworben
              </p>
              <Link to="/jobs">
                <Button className="mt-4 rounded-full">Jobs durchsuchen</Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Applications;
